import api from './api';
import { ReservationHistory } from './types';

/**
 * Reservation History Service
 * Handles all reservation history-related API requests
 */
const reservationHistoryService = {
    /**
     * Get all reservation histories
     * @returns Promise<ReservationHistory[]>
     */
    getAll: async (): Promise<ReservationHistory[]> => {
        try {
            const response = await api.get<{ histories: ReservationHistory[]; total: number; success: boolean }>('/reservation-histories');
            return response.histories || [];
        } catch (error) {
            console.error('Error fetching all reservation histories:', error);
            throw error;
        }
    },

    /**
     * Get reservation history by ID
     * @param id - Numeric ID
     * @returns Promise<ReservationHistory>
     */
    getById: async (id: number): Promise<ReservationHistory> => {
        try {
            const response = await api.get<ReservationHistory>(`/reservation-histories/${id}`);
            return response;
        } catch (error) {
            console.error(`Error fetching reservation history ${id}:`, error);
            throw error;
        }
    },

    /**
     * Get reservation history by public ID
     * @param publicId - Public ID
     * @returns Promise<ReservationHistory>
     */
    getByPublicId: async (publicId: string): Promise<ReservationHistory> => {
        try {
            const response = await api.get<ReservationHistory>(`/reservation-histories/public/${publicId}`);
            return response;
        } catch (error) {
            console.error(`Error fetching reservation history ${publicId}:`, error);
            throw error;
        }
    },

    /**
     * Get reservation histories by reservation ID
     * @param reservationId - Reservation public ID
     * @returns Promise<ReservationHistory[]>
     */
    getByReservationId: async (reservationId: string): Promise<ReservationHistory[]> => {
        try {
            const response = await api.get<{ histories: ReservationHistory[]; total: number; success: boolean }>(`/reservation-histories/reservation/${reservationId}`);
            return response.histories || [];
        } catch (error) {
            console.error(`Error fetching histories for reservation ${reservationId}:`, error);
            throw error;
        }
    },

    /**
     * Get reservation histories by house ID
     * @param houseId - House public ID
     * @returns Promise<ReservationHistory[]>
     */
    getByHouseId: async (houseId: string): Promise<ReservationHistory[]> => {
        try {
            const response = await api.get<{ histories: ReservationHistory[]; total: number; success: boolean }>(`/reservation-histories/house/${houseId}`);
            return response.histories || [];
        } catch (error) {
            console.error(`Error fetching histories for house ${houseId}:`, error);
            throw error;
        }
    },

    /**
     * Get reservation histories by action
     * @param action - Action type
     * @returns Promise<ReservationHistory[]>
     */
    getByAction: async (action: string): Promise<ReservationHistory[]> => {
        try {
            const response = await api.get<{ histories: ReservationHistory[]; total: number; success: boolean }>(`/reservation-histories/action/${action}`);
            return response.histories || [];
        } catch (error) {
            console.error(`Error fetching histories by action ${action}:`, error);
            throw error;
        }
    },

    /**
     * Create new reservation history
     * @param historyData - Reservation history data
     * @returns Promise<ReservationHistory>
     */
    create: async (historyData: Partial<ReservationHistory>): Promise<ReservationHistory> => {
        try {
            const response = await api.post<ReservationHistory>('/reservation-histories', historyData);
            return response;
        } catch (error) {
            console.error('Error creating reservation history:', error);
            throw error;
        }
    },

    /**
     * Update reservation history
     * @param id - History ID (probably public_id based on pattern)
     * @param historyData - Reservation history data to update
     * @returns Promise<ReservationHistory>
     */
    update: async (id: string, historyData: Partial<ReservationHistory>): Promise<ReservationHistory> => {
        try {
            const response = await api.put<ReservationHistory>(`/reservation-histories/${id}`, historyData);
            return response;
        } catch (error) {
            console.error(`Error updating reservation history ${id}:`, error);
            throw error;
        }
    },

    /**
     * Delete reservation history
     * @param id - History ID (probably public_id)
     * @returns Promise<void>
     */
    delete: async (id: string): Promise<void> => {
        try {
            await api.delete(`/reservation-histories/${id}`);
        } catch (error) {
            console.error(`Error deleting reservation history ${id}:`, error);
            throw error;
        }
    }
};

export default reservationHistoryService;